import { PlanType } from './plan-type'

// Envelope-level schema version for the (future) POST api/serviceplan/submit request —
// NOT part of the payload content itself. ServicePlanSubmissionRequestDto.SchemaVersion
// (tsc_schemaversion) is a sibling `int` field alongside `Payload`, confirmed by e2-2's
// shipped contract and e6-2's real section config (schemaVersion = 1).
//
// IMPORTANT deviation from this story's own AC5 text: AC5 asked for a string constant
// '2026.10.0' "matching the design doc's own illustrative value". e6-2 (built after this
// story was drafted) found that value was only ever illustrative — the real shipped
// contract is an int, and explicitly flagged "whoever builds Epic 8's stepper doesn't
// copy the design doc's string literally". This constant follows the shipped contract,
// not the stale AC text. See Dev Agent Record Completion Notes for the full rationale.
export const SERVICE_PLAN_SCHEMA_VERSION = 1

export interface ServicePlanNdis {
  hasNdis: boolean | null
  ndisNumber: string | null
  planManagement: 'self' | 'ndiaManaged' | 'planManaged' | null
  planManager: {
    name: string | null
    address: string | null
    contactName: string | null
    contactEmailPhone: string | null
  }
  applyingForNdis: boolean | null
  privateHealthInsurance: {
    hasPhi: boolean | null
    country: 'australian' | 'overseas' | null
    insurerName: string | null
    memberNo: string | null
  }
  startDate: string | null
  endDate: string | null
  classification: {
    supportPurpose: string[]
    supportCategory: string[]
    registrationGroups: string[]
  }
}

export interface ServicePlanHearingLanguageSupport {
  onFirstSounds: boolean | null
  firstSoundsYear: 'y1' | 'y2' | 'y3' | null
  lslFrequency: 'weekly' | 'fortnightly' | 'every4Weeks' | 'none' | null
  clinicalFrameworkMatch: boolean | null
  clinicalFrameworkMismatchReason: string | null
}

export interface ServicePlanAdditionalServices {
  communityVisitsQty: number
  travelQty: number
  collaborativeCareCfssQty: number
  collaborativeCareAudQty: number
  additionalCommunicationAssessmentQty: number
  startingSchoolReport: boolean
  socialEmotionalAssess: boolean
  speechPerceptionTesting: boolean
  annualSpeechPerception: boolean
  annualSpeechPerceptionReason: string | null
  talkTogether: boolean
  bookingTiming: string | null
}

export interface ServicePlanSupportBlock {
  block: 'none' | 'b3' | 'b6' | 'b9' | null
  packs: number
  joiningLsl: boolean | null
  timing: string | null
}

export interface ServicePlanRecipients {
  primaryEmail: string | null
  additionalEmails: string[]
}

// Mirrors ServicePlan-GuidedFormDesign.md Section 6's draft payload shape. Only
// `planType` and `ndis.hasNdis` are populated by story e8-1 (Step 1) — every other
// field is scaffolding for future e8-2+ stories to populate as their steps ship.
export interface ServicePlanPayload {
  planType: PlanType | null
  ndis: ServicePlanNdis
  baselinePackageConfirmed: boolean
  familyGoals: string | null
  hearingLanguageSupport: ServicePlanHearingLanguageSupport
  additionalServices: ServicePlanAdditionalServices
  cfss: ServicePlanSupportBlock
  audiology: ServicePlanSupportBlock
  continuityPlanning: { notes: string | null }
  recipients: ServicePlanRecipients
}

export function createEmptyServicePlanPayload(): ServicePlanPayload {
  return {
    planType: null,
    ndis: {
      hasNdis: null,
      ndisNumber: null,
      planManagement: null,
      planManager: {
        name: null,
        address: null,
        contactName: null,
        contactEmailPhone: null,
      },
      applyingForNdis: null,
      privateHealthInsurance: {
        hasPhi: null,
        country: null,
        insurerName: null,
        memberNo: null,
      },
      startDate: null,
      endDate: null,
      classification: {
        supportPurpose: [],
        supportCategory: [],
        registrationGroups: [],
      },
    },
    baselinePackageConfirmed: false,
    familyGoals: null,
    hearingLanguageSupport: {
      onFirstSounds: null,
      firstSoundsYear: null,
      lslFrequency: null,
      clinicalFrameworkMatch: null,
      clinicalFrameworkMismatchReason: null,
    },
    additionalServices: {
      communityVisitsQty: 0,
      travelQty: 0,
      collaborativeCareCfssQty: 0,
      collaborativeCareAudQty: 0,
      additionalCommunicationAssessmentQty: 0,
      startingSchoolReport: false,
      socialEmotionalAssess: false,
      speechPerceptionTesting: false,
      annualSpeechPerception: false,
      annualSpeechPerceptionReason: null,
      talkTogether: false,
      bookingTiming: null,
    },
    cfss: { block: null, packs: 0, joiningLsl: null, timing: null },
    audiology: { block: null, packs: 0, joiningLsl: null, timing: null },
    continuityPlanning: { notes: null },
    recipients: { primaryEmail: null, additionalEmails: [] },
  }
}
